/*
 * Copyright (c) Schrocken Inc.
 * File Description: Token debugging utilities
 */

// import jwt_decode from 'jwt-decode';
import jwtDecode from 'jwt-decode';
/**
 * Print token information to console
 * Use this to check token details and expiration
 */
export const debugToken = () => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    console.log('❌ No token found in localStorage');
    return null;
  }

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    
    const tokenInfo = {
      '🔑 Token exists': 'Yes',
      '👤 User': decoded.email || decoded.username || decoded.full_name || 'N/A',
      '🏢 Organization': decoded.organization_name || decoded.organization_id || 'N/A',
      '⏰ Issued at': decoded.iat ? new Date(decoded.iat * 1000).toLocaleString() : 'N/A',
      '⏳ Expires at': decoded.exp ? new Date(decoded.exp * 1000).toLocaleString() : 'Never',
      '⏱️ Current time': new Date(currentTime * 1000).toLocaleString(),
      '📊 Time remaining': decoded.exp ? formatTimeRemaining(decoded.exp - currentTime) : 'No expiration',
      '✅ Is valid': decoded.exp ? (decoded.exp > currentTime ? 'Yes ✓' : 'No ✗ (EXPIRED)') : 'Yes',
      '⚠️ Expiring soon': decoded.exp ? (decoded.exp - currentTime < 300 ? 'Yes (< 5 min)' : 'No') : 'No',
      '📝 Full decoded token': decoded
    };

    console.group('🔐 TOKEN INFORMATION');
    Object.entries(tokenInfo).forEach(([key, value]) => {
      if (key === '📝 Full decoded token') {
        console.log(key + ':', value);
      } else {
        console.log(key + ':', value);
      }
    });
    console.groupEnd();

    return decoded;
  } catch (error) {
    console.error('❌ Error decoding token:', error);
    console.log('Token (first 50 chars):', token.substring(0, 50) + '...');
    return null;
  }
};

/**
 * Format time remaining in human-readable format
 */
const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return '⏱️ EXPIRED';
  
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

/**
 * Watch token expiration in real-time (updates every second)
 */
export const watchTokenExpiration = () => {
  console.log('👁️ Watching token expiration... (Press Ctrl+C or refresh to stop)');
  
  const intervalId = setInterval(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('❌ No token found - stopping watch');
      clearInterval(intervalId);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) {
        console.log('⚠️ Token has no expiration');
        return;
      }

      const currentTime = Date.now() / 1000;
      const remaining = decoded.exp - currentTime;
      
      if (remaining <= 0) {
        console.log('🔴 TOKEN EXPIRED!');
        clearInterval(intervalId);
      } else {
        console.log(`⏳ Time remaining: ${formatTimeRemaining(remaining)}`);
      }
    } catch (error) {
      console.error('Error:', error);
      clearInterval(intervalId);
    }
  }, 1000);

  return () => clearInterval(intervalId);
};

/**
 * Test token expiration handling by simulating an expired token
 * WARNING: This will log you out!
 */
export const testExpiredToken = () => {
  console.warn('⚠️ TESTING EXPIRED TOKEN - You will be logged out!');
  
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.log('❌ No token to test');
    return;
  }

  try {
    const decoded = jwtDecode(token);
    // Create a new token with past expiration
    const expiredDecoded = { ...decoded, exp: Math.floor(Date.now() / 1000) - 100 };
    
    console.log('Original expiry:', new Date(decoded.exp * 1000).toLocaleString());
    console.log('Modified to expired:', new Date(expiredDecoded.exp * 1000).toLocaleString());
    console.log('🔄 Making an API call with expired token should trigger logout...');
    
    // Note: Can't actually modify the token payload without backend
    console.log('💡 To actually test: Wait for real token to expire or manually set exp to past time on backend');
  } catch (error) {
    console.error('Error:', error);
  }
};

// Make functions available globally in browser console
if (typeof window !== 'undefined') {
  window.debugToken = debugToken;
  window.watchTokenExpiration = watchTokenExpiration;
  window.testExpiredToken = testExpiredToken;
}
